import { CombinedError } from 'urql';

import {
  BaseNetworkError,
  GraphQLOperationError,
  isBaseNetworkError,
  NetworkErrorType,
  ServerError,
} from './NetworkErrorClasses';

/**
 * Every error the app can encounter resolves to one of these types.
 * 'abort' — intentionally cancelled request (AbortError), never displayed or logged.
 * 'unknown' — anything we could not classify.
 */
export type ClassifiedErrorType = NetworkErrorType | 'abort' | 'unknown';

export interface ErrorClassification {
  type: ClassifiedErrorType;
  /** HTTP status code, when the error originated from an HTTP response */
  statusCode?: number;
  /** Whether retrying the request could plausibly succeed */
  isRetryable: boolean;
  /** Whether the auth layer should treat this as a session problem */
  shouldLogout: boolean;
  /** User-presentable message provided by the server/GraphQL layer, if any */
  serverMessage?: string;
}

const AUTH_STATUS_CODES = [401, 403];
const AUTH_GRAPHQL_CODES = ['UNAUTHENTICATED', 'FORBIDDEN'];

export const isAbortLikeError = (error: Error): boolean =>
  error.name === 'AbortError' ||
  error.message.includes('timeout') ||
  error.message.includes('aborted');

const classifyStatusCode = (statusCode: number): ClassifiedErrorType => {
  if (statusCode >= 500) {
    return 'server';
  }
  if (AUTH_STATUS_CODES.includes(statusCode)) {
    return 'auth';
  }
  return 'client';
};

const classifyCombinedError = (error: CombinedError): ErrorClassification => {
  const response = error.response as Response | undefined;
  const statusCode =
    response && typeof response.status === 'number'
      ? response.status
      : undefined;

  // urql sets both networkError and response for HTTP errors (4xx/5xx)
  if (statusCode !== undefined && error.networkError) {
    const errorType = classifyStatusCode(statusCode);
    return {
      type: errorType,
      statusCode,
      isRetryable: errorType !== 'auth',
      shouldLogout: errorType === 'auth',
    };
  }

  // networkError without a response: true connectivity failure or client abort
  if (error.networkError && !error.response) {
    if (isAbortLikeError(error.networkError)) {
      return { type: 'timeout', isRetryable: true, shouldLogout: false };
    }
    return { type: 'network', isRetryable: true, shouldLogout: false };
  }

  const hasAuthGraphQLError = error.graphQLErrors?.some((graphQLError) =>
    AUTH_GRAPHQL_CODES.includes(String(graphQLError.extensions?.code)),
  );
  if (hasAuthGraphQLError) {
    return { type: 'auth', isRetryable: false, shouldLogout: true };
  }

  return {
    type: 'graphql',
    isRetryable: true,
    shouldLogout: false,
    serverMessage: error.graphQLErrors?.[0]?.message,
  };
};

const classifyBaseNetworkError = (
  error: BaseNetworkError,
): ErrorClassification => {
  const statusCode =
    'statusCode' in error && typeof error.statusCode === 'number'
      ? error.statusCode
      : undefined;

  let serverMessage: string | undefined;
  if (error instanceof ServerError) {
    serverMessage = error.serverMessage;
  } else if (error instanceof GraphQLOperationError) {
    serverMessage = error.graphqlErrors[0]?.message;
  }

  return {
    type: error.type,
    statusCode,
    isRetryable: error.type !== 'auth',
    shouldLogout: error.type === 'auth',
    serverMessage,
  };
};

/**
 * Single source of truth for turning any error into a typed classification.
 * Used by the centralized handlers (logging policy) and the display layer
 * (copy selection, retry behavior) so the two can never drift apart.
 */
export const classifyError = (error: unknown): ErrorClassification => {
  if (isBaseNetworkError(error)) {
    return classifyBaseNetworkError(error);
  }

  if (error instanceof CombinedError) {
    return classifyCombinedError(error);
  }

  if (error instanceof Error && error.name === 'AbortError') {
    return { type: 'abort', isRetryable: false, shouldLogout: false };
  }

  return { type: 'unknown', isRetryable: true, shouldLogout: false };
};
